export const Product = {
    // NOTE(jvoll): purposely excluding INVALID_PRODUCT since there is no
    // valid reason to set this value. It is strictly used on the
    // server side to denote nulls where we do not want them.

    // In the future, time accounted under OTHER may be broken out
    // into new categories.
    OTHER_PRODUCT: "OTHER_PRODUCT",
    TEST_PREP: "TEST_PREP",
    MISSION: "MISSION",
    MAP: "MAP",
    LIBRARY: "LIBRARY",
    INSTRUCTURE_CANVAS: "INSTRUCTURE_CANVAS",
    ASSESSMENT: "ASSESSMENT",
} as const;

export const Activity = {
    // NOTE(jvoll): purposely excluding INVALID_ACTIVITY since there is no
    // valid reason to set this value. It is strictly used on the
    // server side to denote nulls where we do not want them.
    OTHER_ACTIVITY: "OTHER_ACTIVITY",
    NAVIGATING: "NAVIGATING",
    WATCHING: "WATCHING",
    READING: "READING",
    TEST_TAKING: "TEST_TAKING",
    PROGRAMMING: "PROGRAMMING",
    DISCUSSING: "DISCUSSING",
    PRACTICING: "PRACTICING",
    REVIEWING: "REVIEWING",
    OTHER_LEARNING: "OTHER_LEARNING",
    AI_GUIDE_ACTIVITY: "AI_GUIDE_ACTIVITY",
    AI_GUIDE_WRITING: "AI_GUIDE_WRITING",
    ASSESSING: "ASSESSING",
} as const;

/**
 * NOTE(slim): We have two types for content kinds: a client type and
 * server type. There should be a 1:1 mapping between them.
 *
 * - `LearningTimeContentKind` values are in PascalCase, and are used on
 *    the client.
 * - `LearningTimeContentKindServerFormat` values are in SCREAMING_SNAKE_CASE,
 *    and are expected by learning_time.proto.
 *
 * Conversion between the two happens at the last step before the API
 * request is fired.
 *
 * In general we really don't want this kind of duplication: our client
 * types should reflect the server's whenever possible! But since this
 * library is going to be used all over webapp, where a lot of our code
 * already uses PascalCase ContentKinds like "Article" or "Video", we
 * make an exception to avoid needing to remap ContentKinds at every
 * BigBingo.* call site.
 */
export const ContentKind = {
    // Time on content library pages.
    Topic: "Topic", // default value: "Topic"
    Exercise: "Exercise",
    Article: "Article",
    Video: "Video",

    // Scratchpad types
    Challenge: "Challenge",
    Talkthrough: "Talkthrough",
    Project: "Project",
    Interactive: "Interactive",

    // Other
    TopicQuiz: "TopicQuiz",
    TopicUnitTest: "TopicUnitTest",
    CourseChallenge: "CourseChallenge",
    MasteryChallenge: "MasteryChallenge",
    AIGuideActivity: "AIGuideActivity",
} as const;

// This is the `kind` type expected by learning_time.proto.
export const ContentKindServerFormat = {
    TOPIC: "TOPIC", // default value: "TOPIC"
    EXERCISE: "EXERCISE",
    ARTICLE: "ARTICLE",
    VIDEO: "VIDEO",

    // Scratchpad types
    CHALLENGE: "CHALLENGE",
    TALKTHROUGH: "TALKTHROUGH",
    PROJECT: "PROJECT",
    INTERACTIVE: "INTERACTIVE",

    // Other
    TOPIC_QUIZ: "TOPIC_QUIZ",
    TOPIC_UNIT_TEST: "TOPIC_UNIT_TEST",
    COURSE_CHALLENGE: "COURSE_CHALLENGE",
    MASTERY_CHALLENGE: "MASTERY_CHALLENGE",
    AI_GUIDE_ACTIVITY: "AI_GUIDE_ACTIVITY",
};

export const AssignmentType = {
    UNIT_MASTERY_GOAL: "UNIT_MASTERY_GOAL",
    COURSE_MASTERY_GOAL: "COURSE_MASTERY_GOAL",
    CONTENT_ASSIGNMENT: "CONTENT_ASSIGNMENT",
} as const;

export const ExamGroup = {
    // NOTE(jvoll): INVALID_EXAM_GROUP should ideally never occur however
    // if we have an invalid value for exam group on the client side we'd
    // prefer to send the information me do have rather than dropping the
    // test prep struct completely.
    //
    // We also require it as a default value, in order
    // to prove to TypeScript that merging a set of Partial<LearningTimeTestPrep>
    // structs can produce a complete LearningTimeTestPrep struct.
    INVALID_EXAM_GROUP: "INVALID_EXAM_GROUP",
    SAT_GROUP: "SAT_GROUP",
    LSAT_GROUP: "LSAT_GROUP",
    PRAXIS_GROUP: "PRAXIS_GROUP",
} as const;

export const Exam = {
    SAT: "SAT",
    DIGITAL_SAT: "DIGITAL_SAT",
    LSAT: "LSAT",
    PRAXIS_MATH: "PRAXIS_MATH",
    PRAXIS_READING: "PRAXIS_READING",
    PRAXIS_WRITING: "PRAXIS_WRITING",
} as const;

export const Area = {
    // Catch-all for things not in a given area, like general
    // study strategies (may be associated with any exam)
    OTHER_AREA: "OTHER_AREA",

    // SAT
    READING_AND_WRITING: "READING_AND_WRITING",
    MATH: "MATH",
    ESSAY: "ESSAY",

    // LSAT
    LOGICAL_REASONING: "LOGICAL_REASONING",
    ANALYTICAL_REASONING: "ANALYTICAL_REASONING",
    READING_COMP: "READING_COMP",

    // PRAXIS_MATH
    NUMBER_AND_QUANTITY: "NUMBER_AND_QUANTITY",
    STATISTICS_AND_PROBABILITY: "STATISTICS_AND_PROBABILITY",
    ALGEBRA: "ALGEBRA",
    GEOMETRY: "GEOMETRY",

    // PRAXIS_READING
    ALL_READING_PRACTICE: "ALL_READING_PRACTICE",

    // PRAXIS_WRITING
    OTHER_WRITING_PRACTICE: "OTHER_WRITING_PRACTICE",
    ARGUMENTATIVE_ESSAY: "ARGUMENTATIVE_ESSAY",
    SOURCE_BASED_ESSAY: "SOURCE_BASED_ESSAY",
} as const;

export const EssayActivity = {
    // Understanding the essay prompt.
    PROMPT_REVIEWING: "PROMPT_REVIEWING",

    // Creating an outline for the essay.
    OUTLINING: "OUTLINING",

    // Drafting the essay.
    DRAFTING: "DRAFTING",

    // Revising the essay with Khanmigo feedback.
    REVISING: "REVISING",
} as const;

export const AssessmentCardType = {
    INTRO: "INTRO",
    END: "END",
    QUESTION: "QUESTION",
} as const;

export const AssessmentQuestionType = {
    STANDARD: "STANDARD",
    EXPLAIN_YOUR_THINKING: "EXPLAIN_YOUR_THINKING",
    SIDE_BY_SIDE: "SIDE_BY_SIDE",
} as const;

export const AssessmentEytPart = {
    EYT_EXAMPLE: "EYT_EXAMPLE",
    EYT_QUESTION: "EYT_QUESTION",
    EYT_CHAT: "EYT_CHAT",
} as const;

export type LearningTimeProduct = keyof typeof Product;
export type LearningTimeActivity = keyof typeof Activity;

export type LearningTimeExamGroup = keyof typeof ExamGroup;
export type LearningTimeExam = keyof typeof Exam;

// The name of the testprep product subtype (e.g. SAT Reading)
// that this learning time should credit towards.
export type LearningTimeArea = keyof typeof Area;

export type LearningTimeAssignmentType = keyof typeof AssignmentType;

export type LearningTimeContentKind = keyof typeof ContentKind;
export type LearningTimeContentKindServerFormat =
    keyof typeof ContentKindServerFormat;

export type LearningTimeEssayActivity = keyof typeof EssayActivity;

export type LearningTimeAssessmentCardType = keyof typeof AssessmentCardType;
export type LearningTimeAssessmentQuestionType =
    keyof typeof AssessmentQuestionType;
export type LearningTimeAssessmentEytPart = keyof typeof AssessmentEytPart;
